import EntityLabel from "../../components/EntityLabel";
import { useUnitName } from "../../components/UnitNames";
import {useInventoryLookups} from "../../hooks/useInventoryLookups";
import {lookupProduct} from "../../components/InventoryLines/ProductUnitPicker";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import ManagementPageHTML, {
  IdentityCell,
  StatusBadge,
} from "../../components/ManagementPage/ManagementPageHTML";
import type {
  ManagementColumn,
  ManagementDialogField,
} from "../../components/ManagementPage/ManagementPageTypes";
import type { InventoryItem } from "../../models/InventoryItem";
import { InventoryService } from "../../services/InventoryService";
import "./InventoryCss.css";

const columns: ManagementColumn<InventoryItem>[] = [
  {
    Key: "Product",
    Header: "Product",
    Render: (row) => (
      <IdentityCell Icon="fa-box" Title={<EntityLabel entity="product" code={row.ProductCode} />} Subtitle={row.Code} />
    ),
  },
  {
    Key: "Quantity",
    Header: "On hand",
    Render: (row) => <strong>{row.Quantity}</strong>,
  },
  {
    Key: "Reserved",
    Header: "Reserved",
    Render: (row) => row.ReservedQuantity,
  },
  {
    Key: "Available",
    Header: "Available",
    Render: (row) =>
      row.AvailableQuantity ?? row.Quantity - row.ReservedQuantity,
  },
  {
    Key: "Threshold",
    Header: "Low-stock level",
    Render: (row) => row.LowStockThreshold ?? "—",
  },
  {
    Key: "Status",
    Header: "Status",
    Render: (row) => (
      <StatusBadge
        Status={
          (row.AvailableQuantity ?? row.Quantity - row.ReservedQuantity) <= 0
            ? "OutOfStock"
            : row.LowStockThreshold != null &&
                row.Quantity - row.ReservedQuantity <= row.LowStockThreshold
              ? "LowStock"
              : "InStock"
        }
      />
    ),
  },
];
const fields: ManagementDialogField<InventoryItem>[] = [
  { Key: "Code", Label: "Stock code", DisableAfterNew: true },
  {
    Key: "ProductCode",
    Label: "Product code",
    Required: true,
    DisableAfterNew: true,
  },
  { Key: "Quantity", Label: "Quantity (base units)", Type: "decimal", Required: true },
  {
    Key: "ReservedQuantity",
    Label: "Reserved quantity (base units)",
    Type: "decimal",
    Required: true,
  },
  { Key: "LowStockThreshold", Label: "Low-stock threshold", Type: "decimal" },
  {
    Key: "LowStockNotificationEnabled",
    Label: "Low-stock email",
    Type: "switch",
  },
];
export default function InventoryHTML() {
  const { Warehouse } = useWarehouse();
  const unitName=useUnitName();
  const {products, loading, error} = useInventoryLookups();
  const dialogFields = fields.map(field=>field.Key === "ProductCode" ? {...field,Type:"select" as const,Options:products.map(x=>({display:String(x.Value),value:x.Key,other:x}))} : field);
  const inventoryColumns = [...columns.slice(0,1), {Key:"BaseUnit",Header:"Base unit",Render:(row:InventoryItem)=>{const product=lookupProduct(products,row.ProductCode);const code=product?.BaseUnitCode || product?.UnitPrices?.find(x=>x.BaseQuantity===1)?.UnitCode;return code ? <EntityLabel entity="unit" code={code}/> : "Base units";}}, ...columns.slice(1)];
  return (
    <ManagementPageHTML
      Service={InventoryService(Warehouse?.Code)}
      DefaultValues={{ WareHouse: Warehouse?.Code }}
      Title="Stock"
      Eyebrow="Stock control"
      Description="All quantities use the product base stock unit. Monitor on-hand, reserved and available inventory. Review changes in Stock movement or record a physical inventory count."
      AddLabel="Add stock"
      SearchPlaceholder="Search product code"
      Rows={[]}
      Columns={inventoryColumns}
      CanCreate={false}
      CanEdit={!loading && !error}
      DialogFields={dialogFields}
      GetRowKey={(row) => row.Code || row.ProductCode}
      GetSearchText={(row) => `${row.Code} ${row.ProductCode}`}
      Summaries={[]}
      GetSummaries={(rows, total) => [
        {
          Label: "Stock items",
          Value: total,
          Icon: "fa-boxes-stacked",
          Tone: "Blue",
        },
        {
          Label: "In stock on this page",
          Value: rows.filter(x=>(x.AvailableQuantity ?? x.Quantity-x.ReservedQuantity)>0).length,
          Icon: "fa-warehouse",
          Tone: "Green",
        },
        {
          Label: "Low stock",
          Value: rows.filter(
            (x) =>
              x.LowStockThreshold != null &&
              x.Quantity - x.ReservedQuantity <= x.LowStockThreshold,
          ).length,
          Icon: "fa-triangle-exclamation",
          Tone: "Orange",
        },
        {
          Label: "Out of stock",
          Value: rows.filter((x) => x.Quantity - x.ReservedQuantity <= 0)
            .length,
          Icon: "fa-circle-xmark",
          Tone: "Red",
        },
      ]}
      Filters={[
        {
          Key: "Notification",
          Label: "Notification",
          Options: ["Enabled", "Disabled"],
          GetValue: (row) =>
            row.LowStockNotificationEnabled ? "Enabled" : "Disabled",
        },
      ]}
    />
  );
}
